import { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useAdmin } from '../../context/AdminContext';
import { useAuth } from '../../AuthenticatedApp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faEnvelope, faUserTag, faMapMarkerAlt, faKey } from '@fortawesome/free-solid-svg-icons';
import PasswordResetModal from './PasswordResetModal';
import ActionConfirmationModal from '../shared/ActionConfirmationModal';
import AdminFormModal from '../shared/AdminFormModal';

const EditUserModal = ({ show, onClose, user, onUserUpdated }) => {
  const [isPasswordResetModalVisible, setIsPasswordResetModalVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [areaId, setAreaId] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isWarningModalVisible, setIsWarningModalVisible] = useState(false);
  const { areas, roles } = useAdmin();
  const { hasPermission, user: currentUser } = useAuth();

  useEffect(() => {
    if (user) {
      setSelectedRole(user.roleId || user.role?.id || '');
      setAreaId(user.areaId || '');
    }
  }, [user]);

  const validateForm = () => {
    const nextErrors = {};
    if (hasPermission('assign_roles') && !selectedRole) nextErrors.role = 'Rol seçilmelidir.';
    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    setIsWarningModalVisible(true);
  };

  const handleConfirmUpdate = async () => {
    setLoading(true);
    setIsWarningModalVisible(false);

    const updatedUser = {
      ...user,
      areaId: areaId ? parseInt(areaId, 10) : null,
    };

    if (hasPermission('assign_roles')) {
      const selectedRoleObject = roles.find((role) => role.id === parseInt(selectedRole, 10));
      updatedUser.roleId = parseInt(selectedRole, 10);
      updatedUser.role = selectedRoleObject;
    }

    await onUserUpdated(updatedUser);
    setLoading(false);
  };

  return (
    <>
      <AdminFormModal
        show={show}
        onClose={onClose}
        title="Kullanıcıyı Düzenle"
        subtitle="Rol ve alan atamalarını düzenleyin, gerekirse güvenlik aksiyonu başlatın."
        icon={faEdit}
        footer={(
          <>
            <div className="admin-modal-footer-secondary">
              <Button variant="secondary" onClick={() => setIsPasswordResetModalVisible(true)} className="modal-neutral-btn">
                <FontAwesomeIcon icon={faKey} />
                Şifreyi Sıfırla
              </Button>
              <Button variant="secondary" onClick={onClose} disabled={loading} className="modal-cancel-btn">
                İptal
              </Button>
            </div>
            <div className="admin-modal-footer-primary">
              <Button variant="primary" onClick={handleSubmit} disabled={loading} className="modal-save-btn">
                Kullanıcıyı Güncelle
              </Button>
            </div>
          </>
        )}
      >
        <Form onSubmit={handleSubmit} className="admin-form-section">
          <div className="admin-form-block">
            <div className="admin-form-block-title">
              <FontAwesomeIcon icon={faEnvelope} />
              Hesap Bilgisi
            </div>
            <div className="admin-form-grid">
              <div className="admin-modal-field">
                <Form.Label>E-posta</Form.Label>
                <Form.Control type="email" value={user?.email || ''} disabled className="form-input" />
                <div className="admin-modal-helper">E-posta adresi değiştirilemez.</div>
              </div>

              {hasPermission('assign_roles') ? (
                <div className="admin-modal-field">
                  <Form.Label>
                    <FontAwesomeIcon icon={faUserTag} className="form-icon" />
                    Rol
                  </Form.Label>
                  <Form.Select
                    value={selectedRole}
                    onChange={(event) => setSelectedRole(event.target.value)}
                    className={`form-input ${errors.role ? 'error' : ''}`}
                  >
                    {roles
                      .filter((role) => !(currentUser.userRole?.name === 'venue_admin' && (role.name === 'venue_admin' || role.name === 'venue_owner')))
                      .map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                  </Form.Select>
                  {errors.role ? <div className="error-message">{errors.role}</div> : null}
                </div>
              ) : null}

              <div className="admin-modal-field">
                <Form.Label>
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="form-icon" />
                  Alan
                </Form.Label>
                <Form.Select value={areaId} onChange={(event) => setAreaId(event.target.value)} className="form-input">
                  <option value="">Tüm alanlar</option>
                  {areas.map((area) => (
                    <option key={area.id} value={area.id}>
                      {area.name}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>
          </div>
        </Form>
      </AdminFormModal>

      <PasswordResetModal show={isPasswordResetModalVisible} onClose={() => setIsPasswordResetModalVisible(false)} userEmail={user?.email} />

      <ActionConfirmationModal
        show={isWarningModalVisible}
        onClose={() => setIsWarningModalVisible(false)}
        onConfirm={handleConfirmUpdate}
        title="Kullanıcıyı Güncelle"
        message="Bu kullanıcıyı güncellemek istediğinizden emin misiniz?"
        consequences={['Rol ve alan atamaları yeni değerlere göre güncellenecek.']}
        confirmLabel="Değişiklikleri Kaydet"
        cancelLabel="Vazgeç"
        tone="warning"
        loading={loading}
      />
    </>
  );
};

export default EditUserModal;
